import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { CloseIcon, GoToIcon } from 'assets';
import CancelPalletReportModal from 'components/CancelPalletReportModal/CancelPalletReportModal';
import { ActionsDropdown, StyledBadge, StyledButton, WhiteCard } from 'components/shared';
import { LostDamagedPalletsInDeliverySection } from 'components/SupplierPanel';
import { useErrorDescriptions } from 'constants/errorDescriptions';
import { REPORT_STATUSES, useReportStatuses } from 'constants/reportStatuses';
import { REPORT_TYPES } from 'constants/reportTypes';
import {
   SUPPLIER_CLIENT_DETAILS,
   SUPPLIER_ORDERS_DETAILS,
   SUPPLIER_PLACE_ORDER,
} from 'constants/routes';
import WithAuth from 'hoc/withAuth';
import { BannerData, PalletReportDetailsDTO } from 'interfaces';
import { HTTPService } from 'service';
import { Banner } from 'storybook';
import { errorHandler } from 'utils/errorHandler';
import { formatISODate, getFullAddress } from 'utils/functions';

import styles from './PalletsDetailsPage.module.css';

const PalletsDetailsPage = () => {
   const { t } = useTranslation();
   const [palletReport, setPalletReport] = useState<PalletReportDetailsDTO | null>(null);
   const [banner, setBanner] = useState<BannerData | null>(null);
   const [openPalletReportCancel, setOpenPalletReportCancel] = useState<boolean>(false);
   const [loading, setLoading] = useState<boolean>(true);
   const { id } = useParams();
   const navigate = useNavigate();
   const location = useLocation();

   const { REPORT_STATUSES_AND_LABELS } = useReportStatuses();

   const { DEFAULT_ERROR_DESCRIPTIONS } = useErrorDescriptions();

   useEffect(() => {
      if (id) {
         HTTPService.getLostOrDamagedPalletsReportDetails(id)
            .then(({ data }) => {
               setPalletReport(data);
            })
            .catch(error => handleError(error, DEFAULT_ERROR_DESCRIPTIONS.FETCH_DATA))
            .finally(() => setLoading(false));
      }
   }, [DEFAULT_ERROR_DESCRIPTIONS.FETCH_DATA, id]);

   useEffect(() => {
      if (location.state?.successPlaceOrder) {
         setBanner({
            variant: 'success',
            description: t('supplier_pallets_details_page.success_banner'),
         });
      }
   }, [location.state?.successPlaceOrder, t]);

   const handleCancelPalletReport = () => {
      if (id && palletReport) {
         setLoading(true);
         HTTPService.changeReportStatus(id, REPORT_STATUSES.CANCELED)
            .then(() => {
               setBanner({
                  variant: 'error',
                  description: t('supplier_pallets_details_page.cancel_banner'),
               });
               setPalletReport({ ...palletReport, status: REPORT_STATUSES.CANCELED });
            })
            .catch(error => handleError(error, DEFAULT_ERROR_DESCRIPTIONS.COMPLETE_ACTION))
            .finally(() => {
               setOpenPalletReportCancel(false);
               setLoading(false);
            });
      }
   };

   const handleError = (error: unknown, errorDescription: string) =>
      errorHandler(error, () =>
         setBanner({
            variant: 'error',
            description: errorDescription,
         }),
      );

   return (
      <>
         {openPalletReportCancel && (
            <CancelPalletReportModal
               opened
               onClose={() => setOpenPalletReportCancel(false)}
               loading={loading}
               onAcceptClick={handleCancelPalletReport}
            />
         )}
         {palletReport ? (
            <div className={styles.mainContainer}>
               <div className={styles.headerContainer}>
                  <div>
                     <Link
                        to={SUPPLIER_ORDERS_DETAILS(palletReport.orderId)}
                        className={styles.header}
                     >
                        <h2 className={styles.headerTitle}>
                           {t('supplier_pallets_details_page.details_header', {
                              orderId: palletReport.orderId,
                           })}
                        </h2>
                        <GoToIcon className={styles.goToIcon} />
                     </Link>
                     <div className={styles.subtitles}>
                        <StyledBadge
                           variant={palletReport.status!}
                           text={REPORT_STATUSES_AND_LABELS[palletReport.status!].label}
                        />
                        <p className={styles.date}>
                           {t('supplier_pallets_details_page.report_date', {
                              date: formatISODate(palletReport.createdDate),
                           })}
                        </p>
                     </div>
                  </div>
                  {palletReport.status === REPORT_STATUSES.REPORTED && (
                     <StyledButton
                        variant="outlined-primary"
                        text={t('supplier_pallets_details_page.add_free_order')}
                        onClick={() =>
                           navigate(SUPPLIER_PLACE_ORDER, {
                              state: { palletReportId: palletReport.id },
                           })
                        }
                     />
                  )}
                  {palletReport.status !== REPORT_STATUSES.CANCELED && (
                     <div className={styles.downDropContainer}>
                        <ActionsDropdown
                           options={[
                              {
                                 text: t('cancel_pallet_report_modal.title'),
                                 icon: <CloseIcon />,
                                 onClick: () => setOpenPalletReportCancel(true),
                              },
                           ]}
                        />
                     </div>
                  )}
               </div>
               {banner && (
                  <Banner
                     withCloseIcon
                     fullWidth
                     variant={banner.variant}
                     children={banner.description}
                     onClose={() => setBanner(null)}
                  />
               )}
               <div className={styles.mainContent}>
                  <WhiteCard className={styles.container}>
                     <h4 className={styles.sectionHeader}>
                        {palletReport.assignedUser.companyName}
                     </h4>
                     <Link
                        to={SUPPLIER_CLIENT_DETAILS(palletReport.assignedUser.id)}
                        className={styles.addressLink}
                     >
                        <p>
                           {getFullAddress(
                              palletReport.assignedUser.address,
                              palletReport.assignedUser.zipCode,
                              palletReport.assignedUser.city,
                           )}
                        </p>
                        <GoToIcon />
                     </Link>
                  </WhiteCard>
                  {palletReport.damagedPallets.some(
                     item => item.reportType === REPORT_TYPES.DAMAGE,
                  ) && (
                     <LostDamagedPalletsInDeliverySection
                        className={styles.container}
                        lostDamagedPalletsData={palletReport?.damagedPallets.filter(
                           item => item.reportType === REPORT_TYPES.DAMAGE,
                        )}
                        title={t('regularOrderDetails.damagedPalletsInDelivery')}
                        mode={'PALLET_REPORT_DETAILS'}
                     />
                  )}
                  {palletReport.damagedPallets.some(
                     item => item.reportType === REPORT_TYPES.LOST_OR_DESTRUCTION,
                  ) && (
                     <LostDamagedPalletsInDeliverySection
                        className={styles.container}
                        lostDamagedPalletsData={palletReport?.damagedPallets.filter(
                           item => item.reportType === REPORT_TYPES.LOST_OR_DESTRUCTION,
                        )}
                        title={t('regularOrderDetails.lostDestroyedPalletsInDelivery')}
                        mode={'PALLET_REPORT_DETAILS'}
                     />
                  )}
               </div>
            </div>
         ) : null}
      </>
   );
};

export default WithAuth(PalletsDetailsPage, 'ROLE_SYSTEM_ADMIN');
